import { useCallback, useContext } from 'react';
import _ from 'lodash';

import BaseViewsContext from 'contexts/BaseViewsContext';
import useGetDatasetDefinition from 'hooks/useGetDatasetDefinition';
import isDefined from 'isDefined';

import useScorecardDatasets from './useScorecardDatasets';
import {
  formatIncompatibilityReason,
  getQuickFilterCompatibility,
} from './scorecardQuickFilterFields';

interface CompatibilityResult {
  isCompatible: boolean;
  reason?: string;
}

/**
 * View-time companion to {@link getQuickFilterCompatibility}. Resolves the
 * scorecard's datasets and builds the user-facing tooltip string (the only
 * place that needs `baseViews`/dataset labels) so the pure helper stays free of
 * context.
 */
const useScorecardQuickFilterCompatibility = ({
  scorecard,
}: {
  scorecard: Scorecards.Scorecard;
}) => {
  const scorecardDatasets = useScorecardDatasets(scorecard);
  const getDatasetDefinition = useGetDatasetDefinition();
  const { baseViews, getDatasetLabel } = useContext(BaseViewsContext);

  const getFieldDisplayName = useCallback(
    (field: string): string => {
      const alias = Object.values(baseViews)
        .filter(isDefined)
        .map((view) => view.fields[field])
        .filter(isDefined)
        .map((f) => f.nameAlias)
        .find(isDefined);
      return alias || _.startCase(field);
    },
    [baseViews],
  );

  return useCallback(
    (quickFilter: LegacySavedFilter): CompatibilityResult => {
      const datasets = scorecardDatasets
        .map(getDatasetDefinition)
        .filter(isDefined);

      const compatibility = getQuickFilterCompatibility({
        quickFilter,
        datasets,
      });

      if (compatibility.isCompatible) {
        return { isCompatible: true };
      }

      return {
        isCompatible: false,
        reason: formatIncompatibilityReason({
          incompatibilities: compatibility.incompatibilities,
          getFieldDisplayName,
          getDatasetLabel,
        }),
      };
    },
    [
      scorecardDatasets,
      getDatasetDefinition,
      getFieldDisplayName,
      getDatasetLabel,
    ],
  );
};

export default useScorecardQuickFilterCompatibility;
